//
// Copyright 2022 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import Typography from "@material-ui/core/Typography";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import Portal from "@material-ui/core/Portal";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import Box from "@material-ui/core/Box";
import AutomateDatabasesTable from "src/components/loader/table/AutomateDatabasesTable";
import AutomateDatabaseEditDialog from "src/components/loader/table/AutomateDatabaseEditDialog";
import * as Utils from "eai-configurator-ui/components/Utils";
import YesNoDialog from "eai-configurator-ui/components/configuration/dialog/YesNoDialog";

const AutomateDatabasesPage = ({}) => {
  //
  // State
  const [alertMessage, setAlertMessage] = React.useState(null);
  const [automateDatabasePendingEdit, setAutomateDatabasePendingEdit] = React.useState(
    false
  );
  const [selectedAutomateDatabase, setSelectedAutomateDatabase] = React.useState(null);
  const [tableRefreshCounter, setTableRefreshCounter] = React.useState(0);
  const [runAutomateDatabase, setRunAutomateDatabase] = React.useState({})
  const [runAutomateDatabaseDialogOpen, setRunAutomateDatabaseDialogOpen] = React.useState(false)
  const [runDeleteDatabase, setRunDeleteDatabase] = React.useState({})
  const [runDeleteDatabaseDialogOpen, setRunDeleteDatabaseDialogOpen] = React.useState(false)

  const handleDeleteDatabaseDialog = (database) => {
    setRunDeleteDatabase(database)
    setRunDeleteDatabaseDialogOpen(true);
  };

  const handleDeleteDatabaseNow = (execute) => {
    setRunDeleteDatabaseDialogOpen(false);

    if (execute === true) {
      if (runDeleteDatabase == null || runDeleteDatabase.id == null) {
        setAlertMessage({message: "Failed to find the database to run the deletion on", success: false});
        return;
      }

      Utils.fetchData(
        "loader/deleteAutomationDatabase?id=" + runDeleteDatabase.id,
        false,
        null,
        false,
        false
      ).catch(function (response) {
        Utils.getErrorMessage(response, "Request failed.").then(setAlertMessage);
      });

      setAlertMessage({message: "Database has been submitted for deletion", success: true})
      setTableRefreshCounter(tableRefreshCounter + 1);
    }
  };

  const handleCloseEditedAutomateDatabase = () => {
    setAutomateDatabasePendingEdit(false);
  };

  const handleEditAutomateDatabase = (automateDatabase) => {
    setSelectedAutomateDatabase(automateDatabase);
    setAutomateDatabasePendingEdit(true);
  };

  const handleRunDatabaseDialog = (automateDatabase) => {
    setRunAutomateDatabase(automateDatabase)
    setRunAutomateDatabaseDialogOpen(true);
  };

  const handleRunDatabaseNow = (execute) => {
    setRunAutomateDatabaseDialogOpen(false);
    if (runAutomateDatabase == null || runAutomateDatabase.id == null) {
      setAlertMessage({message: "Failed to find the databvase to run the jobs", success: false});
      return;
    }
    if (execute === true) {
      Utils.fetchData(
        "loader/runDatabaseJob?databaseId=" + runAutomateDatabase.id,
        false,
        null,
        false,
        false
      ).catch(function (response) {
        Utils.getErrorMessage(response, "Request failed.").then(setAlertMessage);
      });

      setAlertMessage({message: "Jobs have been submitted for execution", success: true})
    }
  };

  const handleApplyAutomateDatabaseChanges = () => {
    // Refresh the table which will issue a new fetch and update its contents
    setTableRefreshCounter(tableRefreshCounter + 1);
  };

  return (
    <React.Fragment>
      <div>
        <Typography variant="body2" color="textSecondary">
          <h5>Automate Databases</h5>
          <hr/>
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <h6>Configure the databases for automation</h6>
        </Typography>
        <Typography variant="caption" color="textSecondary">
          In this dialog, you can configure the databases for automation. The automation engine runs every 15 minutes. Once per day,
          global data is exported, and once per day projects that are configured in P6 for automation are exported. If new projects are enabled
          in P6 and you want them exported right away, click the [Run Jobs] button for the corresponding database.
        </Typography>
        <p/>
        <AutomateDatabaseEditDialog
          open={automateDatabasePendingEdit}
          inputAutomateDatabase={selectedAutomateDatabase}
          handleClose={handleCloseEditedAutomateDatabase}
          handleSuccess={handleApplyAutomateDatabaseChanges}
        />
        {alertMessage && (
          <Portal>
            <Snackbar
              open={alertMessage}
              autoHideDuration={6000}
              onClose={() => setAlertMessage(null)}
            >
              <Alert
                onClose={() => setAlertMessage(null)}
                severity={alertMessage && alertMessage.success ? "success" : "error"}
              >
                {alertMessage && (alertMessage.message || "Unknown error")}
              </Alert>
            </Snackbar>
          </Portal>
        )}
        <React.Fragment>
          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            {<AutomateDatabasesTable
              refreshCounter={tableRefreshCounter}
              setAlertMessage={setAlertMessage}
              handleEditAutomateDatabase={handleEditAutomateDatabase}
              handleRunDatabaseNow={handleRunDatabaseDialog}
              handleDeleteDatabase={handleDeleteDatabaseDialog}
            />}
          </Box>
        </React.Fragment>
      </div>
      <YesNoDialog
        title={"Run jobs now?"}
        description={"Are you sure you want to run the pending jobs now? Depending on the database size, this may take several hours."}
        open={runAutomateDatabaseDialogOpen}
        style={{zIndex: 9999}}
        handleYes={() => handleRunDatabaseNow(true)}
        handleNo={() => handleRunDatabaseNow(false)}
      />
      <YesNoDialog
        title={"Delete the database?"}
        description={"Are you sure you want to remove this database? " +
          "This will remove all automated files generated from this database, " +
          "as well as all of its jobs and tasks. The database will be removed in the background " +
          "and will not disappear from the list of databases immediately."}
        open={runDeleteDatabaseDialogOpen}
        style={{zIndex: 9999}}
        handleYes={() => handleDeleteDatabaseNow(true)}
        handleNo={() => handleDeleteDatabaseNow(false)}
      />
    </React.Fragment>
  );
};
export default AutomateDatabasesPage;
